import { useEffect } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { SiPaypal, SiStripe } from "react-icons/si";
import { useLocation, useNavigate } from "react-router-dom";
import CardForm from "./CardForm";
import DropdownSelect from "./DropdownSelect";
import StripeProvider from "./StripeProvider";
import useSubscriptionCheckout, {
  SUBSCRIPTION_FEATURES,
} from "../hooks/useSubscriptionCheckout";

const GATEWAYS = [
  {
    id: "stripe",
    label: "Stripe",
    Icon: SiStripe,
    brand: "#635BFF",
  },
  {
    id: "paypal",
    label: "PayPal",
    Icon: SiPaypal,
    brand: "#003087",
  },
];

const PricingSection = ({
  signinRedirect = "/signin?redirect=/&tag=pricing",
  successPath = "/profile",
  variant = "classic",
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    program,
    selectedPlan,
    setSelectedPlan,
    selectedGateway,
    setSelectedGateway,
    loading,
    clientSecret,
    showModal,
    activating,
    hasActiveAccess,
    enrollNow,
    onCardConfirmed,
    closeCardModal,
  } = useSubscriptionCheckout({ signinRedirect, successPath });

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  if (variant === "v2") {
    const plans = [
      {
        value: "1",
        label: "Monthly",
        price: program?.monthly_price,
        hint: "Billed every month",
      },
      {
        value: "2",
        label: "Yearly",
        price: program?.yearly_price,
        hint: "Best value · billed yearly",
      },
    ];

    return (
      <section className="relative overflow-hidden bg-white py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 left-1/2 h-[420px] w-[520px] -translate-x-1/2 rounded-full bg-[#6756FE]/10 blur-[110px]" />
          <div className="absolute bottom-0 right-0 h-[280px] w-[280px] rounded-full bg-emerald-400/10 blur-[90px]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold tracking-[0.3em] text-[#6756FE] uppercase">
              Pricing
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Start your free trial
            </h2>
            <p className="mt-4 text-slate-500">
              Daily trade alerts, order flow context, and mentorship — cancel anytime.
            </p>
          </div>

          <div className="mt-12 grid items-stretch gap-8 lg:grid-cols-12 lg:gap-10">
            {/* Checkout card */}
            <div className="relative lg:col-span-5">
              <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-b from-[#6756FE]/10 via-white to-white p-7 shadow-xl shadow-slate-200/50 ring-1 ring-[#6756FE]/10 sm:p-8">
                <div
                  className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-[#6756FE]/20 blur-2xl animate-glow-soft"
                  aria-hidden
                />
                <div
                  className="absolute inset-x-0 top-0 h-[3px] animate-border-pulse"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, #6756FE, transparent)",
                  }}
                  aria-hidden
                />

                <div className="relative">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">
                        {program?.name || "Membership"}
                      </h3>
                      <p className="mt-1 text-sm text-[#6756FE]">
                        Join now for immediate access
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold tracking-wide text-emerald-700 uppercase ring-1 ring-emerald-200/80">
                      7-day free trial
                    </span>
                  </div>

                  <p className="mt-4 text-sm text-slate-500">
                    Free 7-day trial, then continue on your chosen plan.
                  </p>

                  {/* Plan pills */}
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {plans.map((plan) => {
                      const active = String(selectedPlan) === plan.value;
                      return (
                        <button
                          key={plan.value}
                          type="button"
                          onClick={() => setSelectedPlan(plan.value)}
                          className={`relative rounded-2xl border px-3 py-3.5 text-left transition ${
                            active
                              ? "border-[#6756FE] bg-[#6756FE]/5 shadow-md shadow-[#6756FE]/10 ring-1 ring-[#6756FE]/30"
                              : "border-slate-200 bg-white hover:border-[#6756FE]/40"
                          }`}
                        >
                          <span className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                            {plan.label}
                          </span>
                          <span className="mt-1 block text-2xl font-bold text-slate-900">
                            {plan.price != null ? `$${plan.price}` : "—"}
                          </span>
                          <span className="mt-0.5 block text-[11px] text-slate-400">
                            {plan.hint}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-sm text-rose-500">Cancel anytime</p>

                  {!hasActiveAccess ? (
                    <div className="mt-6">
                      <p className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                        Pay with
                      </p>
                      <div className="mb-5 flex gap-2">
                        {GATEWAYS.map((gw) => {
                          const active = selectedGateway === gw.id;
                          const Icon = gw.Icon;
                          return (
                            <button
                              key={gw.id}
                              type="button"
                              onClick={() => setSelectedGateway(gw.id)}
                              className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                                active
                                  ? "bg-[#6756FE] text-white shadow-lg shadow-[#6756FE]/25"
                                  : "border border-slate-200 bg-white text-slate-600 hover:border-[#6756FE]/40 hover:text-[#6756FE]"
                              }`}
                            >
                              <Icon
                                className="h-4 w-4 shrink-0"
                                style={active ? undefined : { color: gw.brand }}
                                aria-hidden
                              />
                              {gw.label}
                            </button>
                          );
                        })}
                      </div>

                      {activating ? (
                        <div className="w-full rounded-full bg-slate-100 py-3.5 text-center text-sm font-medium text-slate-600">
                          Activating your subscription…
                        </div>
                      ) : !clientSecret ? (
                        <button
                          type="button"
                          className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#6756FE] py-3.5 text-sm font-bold text-white shadow-lg shadow-[#6756FE]/30 transition hover:bg-[#5646e8] disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={enrollNow}
                          disabled={loading || !program?.id}
                        >
                          {loading ? (
                            <svg
                              className="inline-block h-5 w-5 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10" stroke="currentColor" />
                              <line x1="12" y1="6" x2="12" y2="12" />
                              <line x1="12" y1="18" x2="12" y2="18" />
                            </svg>
                          ) : (
                            <>
                              {(() => {
                                const activeGw =
                                  GATEWAYS.find((g) => g.id === selectedGateway) ||
                                  GATEWAYS[0];
                                const Icon = activeGw.Icon;
                                return <Icon className="h-4 w-4 shrink-0" aria-hidden />;
                              })()}
                              <span>Start free trial</span>
                              <span className="text-white/70">·</span>
                              <span className="font-medium text-white/90">
                                {(
                                  GATEWAYS.find((g) => g.id === selectedGateway) ||
                                  GATEWAYS[0]
                                ).label}
                              </span>
                            </>
                          )}
                        </button>
                      ) : (
                        <div className="mb-2">
                          <StripeProvider>
                            <CardForm
                              isOpen={showModal}
                              onClose={closeCardModal}
                              clientSecret={clientSecret}
                              onComplete={onCardConfirmed}
                              billingName={[user?.first_name, user?.last_name]
                                .filter(Boolean)
                                .join(" ")}
                            />
                          </StripeProvider>
                        </div>
                      )}

                      <p className="mt-4 text-center text-xs text-slate-400">
                        Secure checkout · seats are limited
                      </p>
                    </div>
                  ) : (
                    <div className="mt-6">
                      <p className="mb-4 text-sm text-slate-500">
                        You are already subscribed.
                      </p>
                      <button
                        type="button"
                        className="w-full rounded-full border border-slate-200 bg-slate-50 py-3.5 text-sm font-semibold text-slate-700 transition hover:border-[#6756FE]/40 hover:text-[#6756FE]"
                        onClick={() => navigate("/profile")}
                      >
                        Go to Profile
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="flex flex-col justify-center lg:col-span-7">
              <h3 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Everything in your membership
              </h3>
              <p className="mt-2 max-w-md text-sm text-slate-500">
                Alerts first — plus coaching, community, and live learning to grow with.
              </p>

              <ul className="mt-8 space-y-3">
                {SUBSCRIPTION_FEATURES.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 rounded-2xl border border-slate-200/80 bg-[#f8f7fc] px-4 py-3.5 transition hover:border-[#6756FE]/25 hover:bg-white hover:shadow-md hover:shadow-slate-200/40"
                  >
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#6756FE]/10 text-[#6756FE]">
                      <FaCheckCircle className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm font-medium text-slate-700 sm:text-[15px]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#0e0e11] text-white py-24 px-4 md:px-20">
      <div className="max-w-7xl mx-auto text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold">Pricing</h2>
        <p className="text-gray-400 mt-2 max-w-2xl mx-auto">
          Ready to take your trading skills to next level? Become a part of our
          elite community of traders today.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-8 max-w-7xl mx-auto">
        <div className="bg-black border border-indigo-600 rounded-2xl p-8 w-full lg:w-[420px] shadow-lg">
          <h3 className="text-white text-xl font-bold mb-1">
            {program?.name || "N/A"}
          </h3>
          <p className="text-indigo-400 text-sm mb-1">
            Join now for immediate access
          </p>
          <p className="text-sm text-gray-400 mb-4">Free 7day trial then</p>

          <div className="mb-4">
            <DropdownSelect
              label={"Select Plan"}
              options={[
                {
                  label: "$" + program?.monthly_price + " (1 Month)",
                  value: 1,
                },
                {
                  label: "$" + program?.yearly_price + " (1 Year)",
                  value: 2,
                },
              ]}
              selected={selectedPlan}
              onChange={(value) => setSelectedPlan(value)}
            />
            <p className="text-sm text-red-400 mt-1">Cancel anytime</p>
          </div>

          {!hasActiveAccess ? (
            <span>
              <div className="flex items-center justify-evenly mb-4">
                <div
                  className={
                    "border-2 rounded-full px-4 py-2 cursor-pointer " +
                    (selectedGateway === "stripe"
                      ? "border-black text-black bg-indigo-500"
                      : "border-white-600 text-white-500")
                  }
                  onClick={() => setSelectedGateway("stripe")}
                >
                  Stripe
                </div>
                <div
                  className={
                    "border-2 rounded-full px-4 py-2 cursor-pointer " +
                    (selectedGateway === "paypal"
                      ? "border-black text-black bg-indigo-500"
                      : "border-white-600 text-white-500")
                  }
                  onClick={() => setSelectedGateway("paypal")}
                >
                  Paypal
                </div>
              </div>

              {activating ? (
                <div className="w-full bg-gray-800 text-white py-3 rounded-full font-medium mb-3 text-center">
                  Activating your subscription…
                </div>
              ) : !clientSecret ? (
                <button
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-3 rounded-full font-semibold mb-3"
                  onClick={enrollNow}
                  disabled={loading || !program?.id}
                >
                  {loading ? (
                    <svg
                      className="animate-spin h-5 w-5 mr-3 text-white inline-block"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" stroke="currentColor" />
                      <line x1="12" y1="6" x2="12" y2="12" />
                      <line x1="12" y1="18" x2="12" y2="18" />
                    </svg>
                  ) : (
                    "Enroll Now"
                  )}
                </button>
              ) : (
                <div className="mb-4">
                  <StripeProvider>
                    <CardForm
                      isOpen={showModal}
                      onClose={closeCardModal}
                      clientSecret={clientSecret}
                      onComplete={onCardConfirmed}
                      billingName={[user?.first_name, user?.last_name]
                        .filter(Boolean)
                        .join(" ")}
                    />
                  </StripeProvider>
                </div>
              )}

              <p className="text-sm text-gray-400 mt-4">
                Act fast, seats are limited!
              </p>
            </span>
          ) : (
            <span>
              <p className="text-sm text-gray-400 mb-4">
                You are already subscribed.
              </p>
              <button
                className="w-full bg-gray-800 hover:bg-gray-700 text-white py-3 rounded-full font-medium"
                onClick={() => navigate("/profile")}
              >
                Go to Profile
              </button>
            </span>
          )}
        </div>

        <div className="space-y-4 text-left">
          {SUBSCRIPTION_FEATURES.map((item) => (
            <div key={item} className="flex items-center gap-3 text-white">
              <FaCheckCircle className="text-indigo-400" />
              <span className="text-base">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
